/**
 * useActivities Hook
 *
 * React hooks for activity CRUD operations and analytics.
 * Wraps activityStorage service (Gobodo API) with state management.
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Activity, ActivityFilters, ActivityType, DateRange, SourceApp } from '../types/activity';
import { activityStorage } from '../services/activityStorage';

interface UseActivitiesOptions {
  enabled?: boolean;
}

// Hook for fetching activities with filters
export const useActivities = (filters?: ActivityFilters, options?: UseActivitiesOptions) => {
  const enabled = options?.enabled ?? true;
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<Error | null>(null);
  const requestIdRef = useRef(0);

  // Serialize filters to prevent infinite loops from object reference changes
  const filtersKey = useMemo(() => JSON.stringify(filters || {}), [filters]);

  const withTimeout = useCallback(async <T,>(promise: Promise<T>, timeoutMs: number): Promise<T> => {
    let timeoutHandle: ReturnType<typeof setTimeout> | null = null;
    const timeoutPromise = new Promise<never>((_, reject) => {
      timeoutHandle = setTimeout(() => reject(new Error('Activity request timed out. Please retry.')), timeoutMs);
    });
    try {
      return await Promise.race([promise, timeoutPromise]);
    } finally {
      if (timeoutHandle) window.clearTimeout(timeoutHandle);
    }
  }, []);

  const refetch = useCallback(async () => {
    if (!enabled) {
      setActivities([]);
      setError(null);
      setLoading(false);
      return;
    }
    const reqId = ++requestIdRef.current;
    setLoading(true);
    try {
      const data = await withTimeout(activityStorage.getAll(filters), 15000);
      if (requestIdRef.current !== reqId) return;
      setActivities(data);
      setError(null);
    } catch (err) {
      if (requestIdRef.current !== reqId) return;
      setError(err instanceof Error ? err : new Error('Failed to fetch activities'));
    } finally {
      if (requestIdRef.current !== reqId) return;
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, filtersKey, withTimeout]);

  useEffect(() => {
    if (!enabled) {
      setActivities([]);
      setError(null);
      setLoading(false);
      return;
    }
    refetch();
  }, [enabled, refetch]);

  return { activities, loading, error, refetch };
};

// Hook for activities on a specific record
export const useRecordActivities = (recordId: string, recordType: string) => {
  // Memoize filters to prevent infinite re-renders
  const filters = useMemo(() => ({
    recordId,
    recordType: recordType as any,
  }), [recordId, recordType]);

  return useActivities(filters);
};

// Hook for creating activities
export const useCreateActivity = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const createActivity = useCallback(async (activity: Omit<Activity, 'id' | 'createdAt'>) => {
    setLoading(true);
    try {
      const newActivity = await activityStorage.create(activity);
      setError(null);
      return newActivity;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to create activity');
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  return { createActivity, loading, error };
};

// Hook for updating activities
export const useUpdateActivity = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const updateActivity = useCallback(async (id: string, updates: Partial<Activity>) => {
    setLoading(true);
    try {
      const updatedActivity = await activityStorage.update(id, updates);
      setError(null);
      return updatedActivity;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to update activity');
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  return { updateActivity, loading, error };
};

// Hook for deleting activities
export const useDeleteActivity = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const deleteActivity = useCallback(async (id: string) => {
    setLoading(true);
    try {
      const success = await activityStorage.delete(id);
      setError(null);
      return success;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to delete activity');
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  return { deleteActivity, loading, error };
};

// Hook for activity analytics
export const useActivityAnalytics = (userId: string, dateRange: DateRange) => {
  const [countByType, setCountByType] = useState<Record<ActivityType, number>>({
    email: 0,
    call: 0,
    sms: 0,
    meeting: 0,
    task: 0,
    note: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAnalytics = async () => {
      setLoading(true);
      try {
        const counts = await activityStorage.getCountByType(userId, dateRange);
        setCountByType(counts);
      } finally {
        setLoading(false);
      }
    };
    fetchAnalytics();
  }, [userId, dateRange]);

  return { countByType, loading };
};

// Initialize seed data on app load - NO-OP for Gobodo API
export const useActivitySeedData = () => {
  useEffect(() => {
    activityStorage.seedData();
  }, []);
};

// Advanced filter state interface
export interface AdvancedFilterState {
  typeFilter: ActivityType | 'all';
  userIds: string[];
  sourceApps: SourceApp[];
  dateFrom: string | null;
  dateTo: string | null;
}

// Hook for filtering activities by type (legacy - simple filter)
export const useActivityFilters = (activities: Activity[]) => {
  const [typeFilter, setTypeFilter] = useState<ActivityType | 'all'>('all');

  const filteredActivities = activities.filter((activity) => {
    if (typeFilter === 'all') return true;
    return activity.type === typeFilter;
  });

  return { typeFilter, setTypeFilter, filteredActivities };
};

// Hook for advanced activity filtering (multi-select users, source apps, date range)
export const useAdvancedActivityFilters = (activities: Activity[]) => {
  const [filters, setFilters] = useState<AdvancedFilterState>({
    typeFilter: 'all',
    userIds: [],
    sourceApps: [],
    dateFrom: null,
    dateTo: null,
  });

  const setTypeFilter = useCallback((type: ActivityType | 'all') => {
    setFilters(prev => ({ ...prev, typeFilter: type }));
  }, []);

  const setUserIds = useCallback((userIds: string[]) => {
    setFilters(prev => ({ ...prev, userIds }));
  }, []);

  const setSourceApps = useCallback((sourceApps: SourceApp[]) => {
    setFilters(prev => ({ ...prev, sourceApps }));
  }, []);

  const setDateRange = useCallback((dateFrom: string | null, dateTo: string | null) => {
    setFilters(prev => ({ ...prev, dateFrom, dateTo }));
  }, []);

  const clearFilters = useCallback(() => {
    setFilters({
      typeFilter: 'all',
      userIds: [],
      sourceApps: [],
      dateFrom: null,
      dateTo: null,
    });
  }, []);

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return (
      filters.typeFilter !== 'all' ||
      filters.userIds.length > 0 ||
      filters.sourceApps.length > 0 ||
      filters.dateFrom !== null ||
      filters.dateTo !== null
    );
  }, [filters]);

  // Count active filters
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.typeFilter !== 'all') count++;
    if (filters.userIds.length > 0) count++;
    if (filters.sourceApps.length > 0) count++;
    if (filters.dateFrom || filters.dateTo) count++;
    return count;
  }, [filters]);

  // Apply all filters
  const filteredActivities = useMemo(() => {
    return activities.filter((activity) => {
      // Type filter
      if (filters.typeFilter !== 'all' && activity.type !== filters.typeFilter) {
        return false;
      }

      // User filter (multi-select)
      if (filters.userIds.length > 0 && !filters.userIds.includes(activity.userId)) {
        return false;
      }

      // Source app filter (multi-select)
      if (filters.sourceApps.length > 0) {
        if (!activity.sourceApp || !filters.sourceApps.includes(activity.sourceApp)) {
          return false;
        }
      }

      // Date range filter
      if (filters.dateFrom) {
        const activityDate = new Date(activity.createdAt);
        const fromDate = new Date(filters.dateFrom);
        fromDate.setHours(0, 0, 0, 0);
        if (activityDate < fromDate) return false;
      }

      if (filters.dateTo) {
        const activityDate = new Date(activity.createdAt);
        const toDate = new Date(filters.dateTo);
        toDate.setHours(23, 59, 59, 999);
        if (activityDate > toDate) return false;
      }

      return true;
    });
  }, [activities, filters]);

  // Extract unique users from activities for the filter options
  const availableUsers = useMemo(() => {
    const userMap = new Map<string, string>();
    activities.forEach(a => {
      if (!userMap.has(a.userId)) {
        userMap.set(a.userId, a.userName);
      }
    });
    return Array.from(userMap.entries()).map(([id, name]) => ({ id, name }));
  }, [activities]);

  // Extract unique source apps from activities for the filter options
  const availableSourceApps = useMemo(() => {
    const apps = new Set<SourceApp>();
    activities.forEach(a => {
      if (a.sourceApp) {
        apps.add(a.sourceApp);
      }
    });
    return Array.from(apps);
  }, [activities]);

  return {
    filters,
    setTypeFilter,
    setUserIds,
    setSourceApps,
    setDateRange,
    clearFilters,
    hasActiveFilters,
    activeFilterCount,
    filteredActivities,
    availableUsers,
    availableSourceApps,
  };
};
